"use client";

import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { isNativePlatform, presentTimePicker } from "@/hooks/use-datetime-picker";
import { useTimeFormat } from "@/lib/time-format-context";

interface TimePickerProps {
  time: string;
  onSelect: (time: string) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export function TimePicker({
  time,
  onSelect,
  required,
  disabled,
  className,
}: TimePickerProps) {
  const native = isNativePlatform();
  const timeFormat = useTimeFormat();
  const is24h = timeFormat === "24h";

  const handleNativeClick = async () => {
    if (disabled) return;

    const result = await presentTimePicker({
      value: time || undefined,
      is24h,
    });

    if (result) {
      onSelect(result);
    }
  };

  // Format time for display based on user preference
  const formatTimeDisplay = (timeStr: string): string => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":").map(Number);

    if (is24h) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }

    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
  };

  // Shared styles
  const containerStyles = cn(
    "relative w-full",
    native && "cursor-pointer",
    native && disabled && "cursor-not-allowed opacity-50"
  );

  const inputStyles = cn(
    // Reset iOS native styling
    "appearance-none [-webkit-appearance:none] [-moz-appearance:none]",
    // Base styles matching Input component exactly
    "flex h-9 w-full min-w-0 box-border rounded-md border border-input bg-transparent",
    "px-3 py-1 pr-10 shadow-sm transition-colors",
    "!text-base md:!text-sm leading-normal",
    "placeholder:text-muted-foreground",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-50",
    // Hide native picker icon on web
    !native && "[&::-webkit-calendar-picker-indicator]:opacity-0",
    !native && "[&::-webkit-calendar-picker-indicator]:absolute",
    !native && "[&::-webkit-calendar-picker-indicator]:inset-0",
    !native && "[&::-webkit-calendar-picker-indicator]:w-full",
    !native && "[&::-webkit-calendar-picker-indicator]:h-full",
    !native && "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
    // iOS specific
    "[&::-webkit-date-and-time-value]:text-left",
    "[&::-webkit-datetime-edit]:p-0",
    "[&::-webkit-datetime-edit-fields-wrapper]:p-0",
    // Firefox
    !native && "[&::-moz-calendar-picker-indicator]:opacity-0",
    className
  );

  if (native) {
    // Native: Display-only div that triggers native picker on click
    return (
      <div className={containerStyles} onClick={handleNativeClick}>
        <div className={inputStyles}>
          {time ? (
            formatTimeDisplay(time)
          ) : (
            <span className="text-muted-foreground">Select time</span>
          )}
        </div>
        <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
    );
  }

  // Web: Use native HTML input with 15-minute steps
  return (
    <div className={containerStyles}>
      <input
        type="time"
        step="900"
        value={time}
        onChange={(e) => onSelect(e.target.value)}
        disabled={disabled}
        required={required}
        className={inputStyles}
      />
      <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}
